import React, { useEffect, useState } from "react";
import { 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  Typography, 
  TextField, 
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Divider
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import Navbar from "../../Navbar/Navbar";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import baseURL from "../../config";

// Define document mapping for display purposes
const documentNames = [
  "document1", "document2", "document3", "document4", "document5", "document6",
  "document7", "document8", "document9", "document10", "document11", "document12",
  "document13", "document14", "document15", "document16",
  "document17", "document18", "document19", "document20",
  "document21", "document22", "document23", "document24",
  "document25"
];

const CrewingAgentContractDocument = () => {
  const [documents, setDocuments] = useState({});
  const [crewingAgents, setCrewingAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  

  // Calculate upload progress
  const totalUploaded = Object.keys(documents).length;
  const totalRequired = documentNames.length;
  const uploadProgress = Math.round((totalUploaded / totalRequired) * 100);

  // Handle File Change
  const handleFileChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      const documentKey = `document${index + 1}`;
      setDocuments((prevDocuments) => ({
        ...prevDocuments,
        [documentKey]: {
          file: file,
          name: documentNames[index]
        }
      }));
      toast.success(`${documentNames[index]} added successfully!`);
    }
  };

  // Remove File
  const handleRemoveFile = (index) => {
    const documentKey = `document${index + 1}`;
    setDocuments((prevDocuments) => {
      const newDocuments = { ...prevDocuments };
      delete newDocuments[documentKey];
      return newDocuments;
    });
    toast.info(`${documentNames[index]} removed`);
  };

  // Fetch Crewing Agents from API
  useEffect(() => {
    const fetchCrewingAgents = async () => {
      const token = localStorage.getItem("token");
      try {
        const response = await axios.get(`${baseURL}/api/getcrewingAgentDetails`,{
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setCrewingAgents(response.data);
      } catch (error) {
        console.error("Error fetching crewing agents:", error);
        toast.error("Failed to load crewing agents");
      }
    };
    fetchCrewingAgents();
  }, []);

  // Handle Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (selectedAgent.length === 0) {
      toast.error("Please select at least one Crewing Agent.");
      return;
    }
  
    if (totalUploaded < 1) {
      toast.warning("Please upload at least one document.");
      return;
    }
  
    setIsLoading(true);
    const formData = new FormData();
  
    // ✅ Append the agents array as JSON
    const agentsArray = selectedAgent.map((agent) => ({
      crewingAgentId: agent._id,
      crewingAgentName: agent.agentName,
    }));
  
    formData.append("agents", JSON.stringify(agentsArray));
  
    // ✅ Dynamically append documents for each agent
     // ✅ Append documents directly without any agent reference
  Object.entries(documents).forEach(([key, docInfo]) => {
    if (docInfo.file) {
      formData.append(`${key}`, docInfo.file);       // ✅ Upload document as it is
      formData.append(`${key}Name`, docInfo.name);   // ✅ Upload documentName
    }
  });

  
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${baseURL}/api/uploadCrewContractDocuments`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
  
      toast.success("All documents uploaded successfully!");
      navigate("/CrewingAgentTable")
    } catch (error) {
      console.error("Error uploading documents:", error);
      toast.error(error?.response?.data?.message || "Failed to upload documents.");
    } finally {
      setIsLoading(false);
    }
  };
  
  

  // Check if a document is uploaded
  const isDocumentUploaded = (index) => {
    const documentKey = `document${index + 1}`;
    return !!documents[documentKey];
  };

  // Get document file name if uploaded
  const getDocumentFileName = (index) => {
    const documentKey = `document${index + 1}`;
    return documents[documentKey]?.file.name || '';
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Contract Document Upload
          </Typography>
          
          <form onSubmit={handleSubmit}>
            {/* Document Upload Section */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  Upload Documents
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress 
                    variant="determinate" 
                    value={uploadProgress} 
                    size={40} 
                    sx={{ mr: 2 }} 
                    color={uploadProgress > 75 ? "success" : uploadProgress > 30 ? "info" : "warning"}
                  />
                  <Typography>
                    {uploadProgress}% Complete ({totalUploaded}/{totalRequired})
                  </Typography>
                </Box>
              </Box>
              
              <Grid container spacing={2}>
                {documentNames.map((documentName, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <Card
                      sx={{
                        p: 1,
                        boxShadow: 2,
                        borderRadius: 2,
                        borderLeft: isDocumentUploaded(index) ? '4px solid #4CAF50' : '4px solid #9e9e9e',
                        transition: "all 0.2s",
                        "&:hover": {
                          boxShadow: 4,
                        },
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {documentName}
                          </Typography>
                          {isDocumentUploaded(index) && <CheckCircleIcon color="success" />}
                        </Box>
                        
                        {isDocumentUploaded(index) ? (
                          <Box>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{
                                mb: 1,
                                fontSize: "0.85rem",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDocumentFileName(index)}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                component="label"
                                sx={{ flexGrow: 1 }}
                              >
                                Replace
                                <input
                                  type="file"
                                  hidden
                                  onChange={(e) => handleFileChange(e, index)}
                                />
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => handleRemoveFile(index)}
                              >
                                Remove
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Button
                            variant="contained"
                            component="label"
                            fullWidth
                            sx={{
                              py: 1.5,
                              backgroundColor: "#1976d2",
                              borderRadius: 1.5,
                              "&:hover": { backgroundColor: "#1565c0" },
                            }}
                          >
                            <CloudUploadIcon sx={{ mr: 1 }} />
                            Upload
                            <input
                              type="file"
                              hidden
                              onChange={(e) => handleFileChange(e, index)}
                            />
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />
            
           {/* Crewing Agent Selection */}
{/* Crewing Agent Selection */}
<Box sx={{ my: 4 }}>
  <Typography variant="h6" gutterBottom>
    Select Crewing Agent
  </Typography>
  <Autocomplete
    multiple
    options={crewingAgents || []}  // ✅ Prevent null error
    getOptionLabel={(option) => option.agentName || ""}
    value={selectedAgent}
    onChange={(event, value) => setSelectedAgent(value)}
    isOptionEqualToValue={(option, value) => option._id === value._id}
    renderInput={(params) => (
      <TextField {...params} label="Crewing Agent" fullWidth />
    )}
    renderTags={(value, getTagProps) =>
      value.map((option, index) => (
        <Chip
          key={option._id}
          label={option.agentName}
          {...getTagProps({ index })}
        />
      ))
    }
  />
</Box>


            
            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="success"
                disabled={isLoading || totalUploaded < 1 || !selectedAgent}
                size="large"
                sx={{ minWidth: 150, py: 1.5 }}
              >
                {isLoading ? <CircularProgress size={24} /> : "Submit Documents"}
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
    </>
  );
};

export default CrewingAgentContractDocument;